import React, {useCallback, useEffect, useMemo, useState} from 'react';
import classNames from 'classnames';
import moment from 'moment';
import {IntlShape, useIntl} from 'react-intl';
import {useMediaQuery} from 'react-responsive';
import {CollapseButton} from '../../../components/shared/collapse-button';
import TimiDataGrid from '../../../components/shared/grid';
import {TableTag} from '../../../components/shared/table-tag';
import {
    GridColumnProps,
    GridGroupByProps,
    GridRef,
    GridRowModel,
} from '../../../components/shared/grid/types';
import {getProjects} from '../../../features/projects/filter';
import {canManageOldTimesheetItems} from '../../../features/profile/entityPermissions';
import {
    TimeTrackingImportDataMappingDto,
    TimeTrackingImportEntityKind,
    TimeTrackingImportPreviewDto,
    TimeTrackingImportPreviewRowDto,
    TimeTrackingImportRowStatus,
} from '../../../features/time-tracking-import/types';
import {
    getAllAbsencesTypes,
    getAllShiftsTypes,
    getAllTagsTypes,
} from '../../../features/types';
import {useAppDispatch, useAppSelector} from '../../../hooks/hooks';
import {Lx} from '../../../i18n/consts';
import {
    getEffectiveIssues,
    getEffectiveStatus,
    getFieldDataMappings,
    getMappedImportTags,
    getMappedImportValue,
    getPendingEntityNames,
    getRemainingUnresolvedItems,
} from '../utils/dataMappings';
import {
    buildImportEntityOptions,
    EMPTY_FILE_ROW_CHANGES,
    ImportFileRowChanges,
    ImportRowTarget,
    ManualImportRow,
    ManualImportRowValues,
} from '../utils/manualRows';
import {
    ManualRowAddButton,
    ManualRowContextValue,
    ManualRowProvider,
    ManualRowTag,
} from './TimeTrackingImportManualRowControls';
import TimeTrackingImportManualRowDialog from './TimeTrackingImportManualRowDialog';
import TimeTrackingImportRowActionsMenu from './TimeTrackingImportRowActionsMenu';
import styles from '../time-tracking-import.module.scss';

interface TimeTrackingImportPreviewSectionProps {
    preview: TimeTrackingImportPreviewDto;
    dataMappings?: TimeTrackingImportDataMappingDto[];
    /** Manual rows this preview was built from: their order defines their rows in the file. */
    manualRows?: ManualImportRow[];
    /** What produced every preview row, ordered by the row number. */
    rowTargets?: ImportRowTarget[];
    fileRowChanges?: ImportFileRowChanges;
    onImportRowsChange?: (
        manualRows: ManualImportRow[],
        fileRowChanges: ImportFileRowChanges
    ) => void;
    isManualPreviewPending?: boolean;
}

const getRowKey = (row: TimeTrackingImportPreviewRowDto) =>
    `${row.rowNumber}-${row.employee}-${row.date}`;

const sortPreviewRows = (
    rows: TimeTrackingImportPreviewGridRow[]
): TimeTrackingImportPreviewGridRow[] => {
    const collator = new Intl.Collator(undefined, {
        sensitivity: 'base',
        numeric: true,
    });

    return [...rows].sort((leftRow, rightRow) => {
        const employeeCompare = collator.compare(leftRow.employeeValue, rightRow.employeeValue);

        if (employeeCompare !== 0) {
            return employeeCompare;
        }

        const leftDate = Date.parse(leftRow.date);
        const rightDate = Date.parse(rightRow.date);
        const hasValidDates = !Number.isNaN(leftDate) && !Number.isNaN(rightDate);

        if (hasValidDates && leftDate !== rightDate) {
            return leftDate - rightDate;
        }

        const dateCompare = collator.compare(leftRow.date, rightRow.date);

        if (dateCompare !== 0) {
            return dateCompare;
        }

        return leftRow.rowNumber - rightRow.rowNumber;
    });
};

const NEW_ENTITY_WARNING_CODES = new Set([
    'new_employee_will_be_created',
    'new_project_will_be_created',
    'new_shift_type_will_be_created',
    'new_absence_type_will_be_created',
    'new_tag_will_be_created',
]);

type TimeTrackingImportPreviewGridRow = TimeTrackingImportPreviewRowDto & {
    id: string;
    status: TimeTrackingImportRowStatus;
    employeeValue: string;
    projectOrAbsenceValue: string;
    typeValue: string;
    tagsValue: string;
    newEntityWarnings: ReturnType<typeof getEffectiveIssues>['warnings'];
    regularWarnings: ReturnType<typeof getEffectiveIssues>['warnings'];
    errors: ReturnType<typeof getEffectiveIssues>['errors'];
    /** What produced the row: a file row or a record added by hand. */
    target?: ImportRowTarget;
    /** Key of the row origin label: added by hand / rewritten. */
    originTagLabelId?: string;
};

const IMPORT_DATE_FORMATS = [
    'YYYY-MM-DD',
    'DD.MM.YYYY',
    'D.M.YYYY',
    'DD/MM/YYYY',
    'M/D/YYYY',
    'YYYY/MM/DD',
    'DD-MM-YYYY',
];

const normalizeImportRowDate = (value: string) => {
    const parsedDate = moment(value, [...IMPORT_DATE_FORMATS, moment.ISO_8601], false);

    return parsedDate.isValid() ? parsedDate.format('YYYY-MM-DD') : '';
};

const isAbsenceRow = (row: TimeTrackingImportPreviewGridRow) =>
    String(row.entityKind ?? '').trim().toLowerCase() === 'absence'
    || row.entityKind === TimeTrackingImportEntityKind.Absence;

/**
 * Values of a preview row in the shape a manual record keeps them: for an absence the type lives
 * in the "Project/Absence" column, and neither shift type nor tags apply.
 */
const getImportRowValues = (row: TimeTrackingImportPreviewGridRow): ManualImportRowValues => {
    const isAbsence = isAbsenceRow(row);

    return {
        employeeValue: row.employeeValue,
        kind: isAbsence ? 'absence' : 'shift',
        date: normalizeImportRowDate(row.date),
        hours: row.hours,
        projectOrAbsence: isAbsence
            ? row.typeValue || row.projectOrAbsenceValue
            : row.projectOrAbsenceValue,
        type: isAbsence ? '' : row.typeValue,
        note: row.note,
        tags: isAbsence
            ? []
            : row.tagsValue.split(',').map((tag) => tag.trim()).filter(Boolean),
        isOvertime: !isAbsence && Boolean(row.isOvertime),
    };
};

const getStatusTagClassName = (status: TimeTrackingImportRowStatus): string => {
    switch (status) {
        case TimeTrackingImportRowStatus.Ready:
            return styles.successTableTag;
        case TimeTrackingImportRowStatus.Warning:
            return styles.warningTableTag;
        default:
            return styles.errorTableTag;
    }
};

const getStatusLabel = (status: TimeTrackingImportRowStatus, intl: IntlShape) => {
    switch (status) {
        case TimeTrackingImportRowStatus.Ready:
            return intl.formatMessage({id: Lx.TimeTrackingImport.PREVIEW_STATUS_READY});
        case TimeTrackingImportRowStatus.Warning:
            return intl.formatMessage({id: Lx.TimeTrackingImport.PREVIEW_STATUS_WARNING});
        default:
            return intl.formatMessage({id: Lx.TimeTrackingImport.PREVIEW_STATUS_ERROR});
    }
};

const isNewEntityWarning = (warning: TimeTrackingImportPreviewGridRow['newEntityWarnings'][number]) =>
    NEW_ENTITY_WARNING_CODES.has(warning.code) || warning.code.startsWith('mapped_');

const rowHasNewEntityWarning = (
    row: TimeTrackingImportPreviewGridRow,
    fields: string[]
) => row.newEntityWarnings.some((warning) => fields.includes(warning.field));

const getNewEntityTag = (intl: IntlShape) => (
    <TableTag
        label={intl.formatMessage({id: Lx.TimeTrackingImport.PREVIEW_NEW_ENTITY_TAG})}
        className={classNames(styles.previewNewEntityTag, styles.accentTableTag)}
        classes={{label: styles.previewNewEntityTagLabel}}
    />
);

const getEntityValueCell = (
    value: string,
    row: TimeTrackingImportPreviewGridRow,
    fields: string[],
    intl: IntlShape
) => (
    <div className={styles.previewEntityCell}>
        <span className={styles.previewEntityCellText}>{value || '-'}</span>
        {rowHasNewEntityWarning(row, fields) ? getNewEntityTag(intl) : null}
    </div>
);

const getIssueGroup = (row: TimeTrackingImportPreviewGridRow, intl: IntlShape) => (
    <div className={styles.issueGroup}>
        {row.originTagLabelId ? <ManualRowTag labelId={row.originTagLabelId} /> : null}
        {row.errors.map((issue) => (
            <span
                key={`${row.rowNumber}-error-${issue.code}`}
                className={styles.errorIssue}
            >
                {issue.message}
            </span>
        ))}
        {row.regularWarnings.map((warning) => (
            <span
                key={`${row.rowNumber}-warning-${warning.code}`}
                className={styles.warningIssue}
            >
                {warning.message}
            </span>
        ))}
        {row.errors.length < 1 && row.regularWarnings.length < 1 && (
            <span className={styles.noIssues}>
                {intl.formatMessage({id: Lx.TimeTrackingImport.PREVIEW_NO_ISSUES})}
            </span>
        )}
    </div>
);

const PreviewEmployeeGroupRow: React.FC<{
    title: string;
    groupRows: TimeTrackingImportPreviewGridRow[];
}> = ({title, groupRows: rows}) => {
    const intl = useIntl();
    const readyRows = rows.filter((row) => row.status === TimeTrackingImportRowStatus.Ready).length;
    const warningRows = rows.filter((row) => row.status === TimeTrackingImportRowStatus.Warning).length;
    const errorRows = rows.filter((row) => row.status === TimeTrackingImportRowStatus.Error).length;
    const totalHours = rows.reduce((sum, row) => {
        const hours = Number.parseFloat(String(row.hours).replace(',', '.'));
        return Number.isNaN(hours) ? sum : sum + hours;
    }, 0);

    return (
        <div className={styles.previewGroupRow}>
            <div className={styles.previewGroupEmployee}>
                <span className={styles.previewGroupEmployeeText}>{title}</span>
                {rows.some((row) => rowHasNewEntityWarning(row, ['employee'])) ? getNewEntityTag(intl) : null}
            </div>
            <div className={styles.previewGroupStats}>
                <TableTag
                    label={intl.formatMessage(
                        {id: Lx.TimeTrackingImport.PREVIEW_GROUP_HOURS},
                        {count: totalHours}
                    )}
                    className={classNames(styles.previewGroupTag, styles.secondaryTableTag)}
                    classes={{label: styles.previewGroupTagLabel}}
                />
                <TableTag
                    label={intl.formatMessage(
                        {id: Lx.TimeTrackingImport.PREVIEW_GROUP_ROWS},
                        {count: rows.length}
                    )}
                    className={classNames(styles.previewGroupTag, styles.secondaryTableTag)}
                    classes={{label: styles.previewGroupTagLabel}}
                />
                <TableTag
                    label={intl.formatMessage(
                        {id: Lx.TimeTrackingImport.PREVIEW_GROUP_READY},
                        {count: readyRows}
                    )}
                    className={classNames(styles.previewGroupTag, styles.successTableTag)}
                    classes={{label: styles.previewGroupTagLabel}}
                />
                <TableTag
                    label={intl.formatMessage(
                        {id: Lx.TimeTrackingImport.PREVIEW_GROUP_WARNINGS},
                        {count: warningRows}
                    )}
                    className={classNames(styles.previewGroupTag, styles.warningTableTag)}
                    classes={{label: styles.previewGroupTagLabel}}
                />
                <TableTag
                    label={intl.formatMessage(
                        {id: Lx.TimeTrackingImport.PREVIEW_GROUP_ERRORS},
                        {count: errorRows}
                    )}
                    className={classNames(styles.previewGroupTag, styles.errorTableTag)}
                    classes={{label: styles.previewGroupTagLabel}}
                />
                <ManualRowAddButton employeeValue={title} />
            </div>
        </div>
    );
};

const getPreviewColumns = (
    isNarrowScreen: boolean,
    isMediumDesktop: boolean,
    isCompactDesktop: boolean,
    hasOvertimeRows: boolean,
    intl: IntlShape
): GridColumnProps[] => [
    {
        field: intl.formatMessage({id: Lx.TimeTrackingImport.PREVIEW_COLUMN_STATUS}),
        isHeaderCollapseButton: true,
        width: 128,
        value: (row) => (row as TimeTrackingImportPreviewGridRow).status,
        displayValue: (_value, row) => {
            const status = (row as TimeTrackingImportPreviewGridRow).status;

            return (
                <TableTag
                    label={getStatusLabel(status, intl)}
                    className={classNames(styles.statusBadge, getStatusTagClassName(status))}
                    classes={{label: styles.statusBadgeLabel}}
                />
            );
        },
    },
    {
        field: intl.formatMessage({id: Lx.TimeTrackingImport.PREVIEW_COLUMN_DATE}),
        width: 96,
        value: (row) => (row as TimeTrackingImportPreviewGridRow).date,
    },
    {
        field: intl.formatMessage({id: Lx.TimeTrackingImport.PREVIEW_COLUMN_HOURS}),
        width: 64,
        type: 'number',
        alignment: 'start',
        value: (row) => (row as TimeTrackingImportPreviewGridRow).hours,
        cellRender: (_field, row) => (
            <div className={styles.previewGridHoursValue}>
                {(row as TimeTrackingImportPreviewGridRow).hours}
            </div>
        ),
    },
    {
        // The column shows up only when the import has overtime rows: for ordinary files the preview
        // looks exactly as before.
        field: intl.formatMessage({id: Lx.TimeTrackingImport.PREVIEW_COLUMN_OVERTIME}),
        width: 44,
        visible: hasOvertimeRows,
        alignment: 'center',
        value: (row) => (row as TimeTrackingImportPreviewGridRow).isOvertime ?? false,
        displayValue: (_value, row) => (row as TimeTrackingImportPreviewGridRow).isOvertime
            ? (
                <span
                    className={styles.previewOvertimeMark}
                    title={intl.formatMessage({id: Lx.Schedule.IS_OVERTIME})}
                    aria-label={intl.formatMessage({id: Lx.Schedule.IS_OVERTIME})}
                >
                    +
                </span>
            )
            : '',
    },
    {
        field: intl.formatMessage({id: Lx.TimeTrackingImport.PREVIEW_COLUMN_PROJECT_ABSENCE}),
        flex: {grow: isNarrowScreen || isMediumDesktop ? 5 : 4},
        value: (row) => (row as TimeTrackingImportPreviewGridRow).projectOrAbsenceValue,
        displayValue: (value, row) => getEntityValueCell(
            String(value ?? ''),
            row as TimeTrackingImportPreviewGridRow,
            ['projectOrAbsence'],
            intl
        ),
    },
    {
        field: intl.formatMessage({id: Lx.TimeTrackingImport.PREVIEW_COLUMN_TYPE}),
        width: isCompactDesktop ? 60 : isNarrowScreen ? 72 : isMediumDesktop ? 84 : 108,
        value: (row) => (row as TimeTrackingImportPreviewGridRow).typeValue,
        displayValue: (value, row) => getEntityValueCell(
            String(value ?? ''),
            row as TimeTrackingImportPreviewGridRow,
            ['type'],
            intl
        ),
    },
    {
        field: intl.formatMessage({id: Lx.TimeTrackingImport.PREVIEW_COLUMN_COMMENT}),
        flex: {grow: isNarrowScreen || isMediumDesktop ? 5 : 4},
        value: (row) => (row as TimeTrackingImportPreviewGridRow).note,
        displayValue: (value) => value || '-',
    },
    {
        field: intl.formatMessage({id: Lx.TimeTrackingImport.PREVIEW_COLUMN_TAGS}),
        width: isCompactDesktop ? 84 : isNarrowScreen || isMediumDesktop ? 108 : 132,
        value: (row) => (row as TimeTrackingImportPreviewGridRow).tagsValue,
        displayValue: (value, row) => getEntityValueCell(
            String(value ?? ''),
            row as TimeTrackingImportPreviewGridRow,
            ['tags'],
            intl
        ),
    },
    {
        field: intl.formatMessage({id: Lx.TimeTrackingImport.PREVIEW_COLUMN_ISSUES}),
        width: isCompactDesktop ? 112 : isNarrowScreen ? 128 : isMediumDesktop ? 152 : 240,
        cellClassName: styles.previewGridIssueCell,
        value: (row) => [
            ...(row as TimeTrackingImportPreviewGridRow).errors,
            ...(row as TimeTrackingImportPreviewGridRow).regularWarnings,
        ],
        displayValue: (_value, row) => getIssueGroup(row as TimeTrackingImportPreviewGridRow, intl),
    },
];

const getPreviewGroupBy = (): GridGroupByProps => ({
    rowGroupingModel: ['employeeValue'],
    groupByRenderer: (_groupField, groupFieldValue, value) => (
        <PreviewEmployeeGroupRow
            title={groupFieldValue}
            groupRows={value as TimeTrackingImportPreviewGridRow[]}
        />
    ),
});

const buildTemplateColumns = (columns: GridColumnProps[]): React.CSSProperties => {
    const values: string[] = [];

    columns
        .filter((column) => column.visible === undefined || column.visible)
        .forEach((column) => {
            if (column.flex !== undefined) {
                const grow = typeof column.flex === 'boolean' ? 1 : column.flex.grow;
                values.push(`minmax(0, ${grow}fr)`);
                return;
            }

            values.push(`${column.width ?? 150}px`);
        });

    return {gridTemplateColumns: values.join(' ')};
};

const createHeaderCells = (
    columns: GridColumnProps[],
    isAnyRowExpanded: boolean,
    onCollapseToggle: () => void,
    intl: IntlShape
) => columns
    .filter((column) => column.visible === undefined || column.visible)
    .map((column, index) => {
        if (column.isHeaderCollapseButton) {
            return (
                <div
                    key={column.field}
                    className={classNames(styles.previewGridHeaderCell, styles.previewGridCollapseHeaderCell)}
                >
                    <CollapseButton
                        displayText={intl.formatMessage({
                            id: isAnyRowExpanded
                                ? Lx.TimeTrackingImport.PREVIEW_COLLAPSE_ALL
                                : Lx.TimeTrackingImport.PREVIEW_EXPAND_ALL,
                        })}
                        isExpanded={isAnyRowExpanded}
                        onExpandClick={onCollapseToggle}
                        containerClassName={styles.previewGridCollapseButtonContainer}
                        displayTextClassName={styles.previewGridCollapseButtonText}
                    />
                </div>
            );
        }

        return (
            <div
                key={`${column.field}-${index}`}
                className={classNames(styles.previewGridHeaderCell, {
                    [styles.previewGridNumericHeaderCell]: column.type === 'number' || column.alignment === 'end',
                    [styles.previewGridCenteredHeaderCell]: column.alignment === 'center',
                })}
            >
                <span className={styles.previewGridHeaderText}>{column.field}</span>
            </div>
        );
    });

export default function TimeTrackingImportPreviewSection({
    preview,
    dataMappings = [],
    manualRows = [],
    rowTargets = [],
    fileRowChanges = EMPTY_FILE_ROW_CHANGES,
    onImportRowsChange,
    isManualPreviewPending = false,
}: TimeTrackingImportPreviewSectionProps) {
    const intl = useIntl();
    const dispatch = useAppDispatch();
    const profile = useAppSelector((store) => store.profile);
    const projects = useAppSelector((store) => store.eProjects);
    const shiftTypes = useAppSelector((store) => store.types.shifts.items);
    const absenceTypes = useAppSelector((store) => store.types.absences.items);
    const tagTypes = useAppSelector((store) => store.types.tags.items);
    const isNarrowScreen = useMediaQuery({maxWidth: 1360});
    const isMediumDesktop = useMediaQuery({minWidth: 1361, maxWidth: 1500});
    const isCompactDesktop = useMediaQuery({minWidth: 1001, maxWidth: 1180});
    const [isAnyRowExpanded, setIsAnyRowExpanded] = useState(false);
    const [editorState, setEditorState] = useState<{
        employeeValue: string;
        target: ImportRowTarget | null;
        values: ManualImportRowValues | null;
    } | null>(null);
    const [actionsMenu, setActionsMenu] = useState<{
        position: {top: number; left: number};
        row: TimeTrackingImportPreviewGridRow;
    } | null>(null);
    const gridRef = React.useRef<GridRef>(null);
    const targetByRowNumber = useMemo(() => {
        // The generated file rows come in a known order (see buildImportRowTargets), and the server
        // numbers them by position — so they can be matched by row number. If the snapshot does not match
        // the preview, rows simply get no actions.
        const rowNumbers = preview.rows
            .map((row) => row.rowNumber)
            .sort((left, right) => left - right);

        if (rowTargets.length !== rowNumbers.length) {
            return new Map<number, ImportRowTarget>();
        }

        return new Map(rowNumbers.map((rowNumber, index) => [rowNumber, rowTargets[index]]));
    }, [preview.rows, rowTargets]);
    const rows = useMemo<TimeTrackingImportPreviewGridRow[]>(() => preview.rows.map((row) => {
        const effectiveIssues = getEffectiveIssues(row, dataMappings, intl);
        const status = getEffectiveStatus(effectiveIssues.errors, effectiveIssues.warnings);
        const newEntityWarnings = effectiveIssues.warnings.filter(
            (warning) => isNewEntityWarning(warning)
        );
        const regularWarnings = effectiveIssues.warnings.filter(
            (warning) => !isNewEntityWarning(warning)
        );
        const employeeValue = getMappedImportValue(
            row.employee,
            getFieldDataMappings(dataMappings, 'employee')
        );
        const projectOrAbsenceValue = getMappedImportValue(
            row.projectOrAbsence,
            getFieldDataMappings(dataMappings, 'projectOrAbsence')
        );
        const typeValue = row.type
            ? getMappedImportValue(row.type, getFieldDataMappings(dataMappings, 'type'))
            : '';
        const tagsValue = row.tags
            ? getMappedImportTags(row.tags, getFieldDataMappings(dataMappings, 'tags'))
            : '';

        const target = targetByRowNumber.get(row.rowNumber);
        const originTagLabelId = target?.type === 'manual'
            ? Lx.TimeTrackingImport.MANUAL_ROW_TAG
            : target?.type === 'file' && fileRowChanges.overrides[target.index]
                ? Lx.TimeTrackingImport.MANUAL_ROW_EDITED_TAG
                : undefined;
        // For rows filled by hand the page state is the source of truth, not the server response: the
        // flag shows up right after saving and does not depend on the preview echoing it back.
        const manualValues = target?.type === 'manual'
            ? manualRows.find((manualRow) => manualRow.id === target.id)
            : target?.type === 'file'
                ? fileRowChanges.overrides[target.index]
                : undefined;

        return {
            ...row,
            id: getRowKey(row),
            status,
            employeeValue,
            projectOrAbsenceValue,
            typeValue,
            tagsValue,
            newEntityWarnings,
            regularWarnings,
            errors: effectiveIssues.errors,
            target,
            originTagLabelId,
            isOvertime: manualValues?.isOvertime ?? row.isOvertime ?? false,
        };
    }), [
        dataMappings,
        fileRowChanges.overrides,
        intl,
        manualRows,
        preview.rows,
        targetByRowNumber,
    ]);
    const sortedRows = useMemo(() => sortPreviewRows(rows), [rows]);
    const hasOvertimeRows = useMemo(() => rows.some((row) => row.isOvertime), [rows]);
    const columns = useMemo(
        () => getPreviewColumns(
            isNarrowScreen,
            isMediumDesktop,
            isCompactDesktop,
            hasOvertimeRows,
            intl
        ),
        [hasOvertimeRows, intl, isCompactDesktop, isMediumDesktop, isNarrowScreen]
    );
    const groupBy = useMemo(() => getPreviewGroupBy(), []);
    const headerTemplateColumns = useMemo(() => buildTemplateColumns(columns), [columns]);
    const headerCells = useMemo(() => createHeaderCells(
        columns,
        isAnyRowExpanded,
        () => gridRef.current?.collapseRows(),
        intl
    ), [columns, intl, isAnyRowExpanded]);
    const effectiveRows = useMemo(() => preview.rows.map((row) => {
        const issues = getEffectiveIssues(row, dataMappings, intl);
        const status = getEffectiveStatus(issues.errors, issues.warnings);

        return {
            rowNumber: row.rowNumber,
            status,
        };
    }), [dataMappings, intl, preview.rows]);
    const remainingUnresolved = useMemo(
        () => getRemainingUnresolvedItems(preview, dataMappings),
        [dataMappings, preview]
    );
    const isManualRowsSupported = Boolean(onImportRowsChange);
    const manualRowOptions = useMemo(() => ({
        projects: buildImportEntityOptions(
            projects,
            getPendingEntityNames(preview, dataMappings, 'project')
        ),
        absenceTypes: buildImportEntityOptions(
            absenceTypes,
            getPendingEntityNames(preview, dataMappings, 'absenceType')
        ),
        shiftTypes: buildImportEntityOptions(
            shiftTypes,
            getPendingEntityNames(preview, dataMappings, 'shiftType')
        ),
        tags: buildImportEntityOptions(
            tagTypes,
            getPendingEntityNames(preview, dataMappings, 'tag')
        ),
    }), [absenceTypes, dataMappings, preview, projects, shiftTypes, tagTypes]);
    const manualRowDateRange = useMemo(() => ({
        maxDate: moment().startOf('day'),
        minDate: canManageOldTimesheetItems(profile)
            ? moment().startOf('day').subtract(1, 'year')
            : moment().startOf('month'),
    }), [profile]);
    const employeeValues = useMemo(() => {
        const values: string[] = [];

        sortedRows.forEach((row) => {
            if (row.employeeValue && !values.includes(row.employeeValue)) {
                values.push(row.employeeValue);
            }
        });

        return values;
    }, [sortedRows]);
    // A new entry defaults to the date of the employee's last row: entries are usually added to the
    // same period as the file.
    const editorDefaultDate = useMemo(() => {
        const lastDate = sortedRows
            .filter((row) => row.employeeValue === editorState?.employeeValue)
            .map((row) => moment(row.date, ['YYYY-MM-DD', 'DD.MM.YYYY', 'M/D/YYYY', moment.ISO_8601]))
            .filter((date) => date.isValid() && !date.isAfter(manualRowDateRange.maxDate, 'day'))
            .pop();

        return (lastDate ?? manualRowDateRange.maxDate).format('YYYY-MM-DD');
    }, [editorState?.employeeValue, manualRowDateRange.maxDate, sortedRows]);
    const editedRow = useMemo<ManualImportRow | null>(
        () => editorState?.values ? {...editorState.values, id: 'edited-import-row'} : null,
        [editorState?.values]
    );
    const handleManualRowAdd = useCallback((employeeValue: string) => {
        setEditorState({employeeValue, target: null, values: null});
    }, []);
    const handleRowClick = useCallback((
        row: GridRowModel,
        event: React.MouseEvent<HTMLDivElement>
    ) => {
        const previewRow = row as TimeTrackingImportPreviewGridRow;

        if (!isManualRowsSupported || isManualPreviewPending || !previewRow.target) {
            return;
        }

        // The menu is opened by clicking the row itself, so it is anchored to the click point.
        const position = {top: event.clientY, left: event.clientX};

        setActionsMenu({row: previewRow, position});
    }, [isManualPreviewPending, isManualRowsSupported]);
    const handleRowEdit = useCallback((row: TimeTrackingImportPreviewGridRow) => {
        const target = row.target;

        if (!target) {
            return;
        }

        const storedValues = target.type === 'manual'
            ? manualRows.find((manualRow) => manualRow.id === target.id)
            : fileRowChanges.overrides[target.index];

        setEditorState({
            employeeValue: row.employeeValue,
            target,
            values: storedValues ?? getImportRowValues(row),
        });
    }, [fileRowChanges.overrides, manualRows]);
    const handleRowRemove = useCallback((row: TimeTrackingImportPreviewGridRow) => {
        if (!row.target) {
            return;
        }

        if (row.target.type === 'manual') {
            const removedId = row.target.id;

            onImportRowsChange?.(
                manualRows.filter((manualRow) => manualRow.id !== removedId),
                fileRowChanges
            );
            return;
        }

        const {[row.target.index]: removedOverride, ...restOverrides} = fileRowChanges.overrides;
        void removedOverride;

        onImportRowsChange?.(manualRows, {
            excludedIndexes: [...fileRowChanges.excludedIndexes, row.target.index],
            overrides: restOverrides,
        });
    }, [fileRowChanges, manualRows, onImportRowsChange]);
    const handleRowSave = useCallback((savedRow: ManualImportRow) => {
        const target = editorState?.target;

        setEditorState(null);

        if (!target) {
            onImportRowsChange?.([...manualRows, savedRow], fileRowChanges);
            return;
        }

        if (target.type === 'manual') {
            onImportRowsChange?.(
                manualRows.map((manualRow) => manualRow.id === target.id
                    ? {...savedRow, id: target.id}
                    : manualRow),
                fileRowChanges
            );
            return;
        }

        const {id: _savedRowId, ...savedValues} = savedRow;
        void _savedRowId;

        onImportRowsChange?.(manualRows, {
            ...fileRowChanges,
            overrides: {...fileRowChanges.overrides, [target.index]: savedValues},
        });
    }, [editorState?.target, fileRowChanges, manualRows, onImportRowsChange]);
    const handleRestoreRemovedRows = useCallback(() => {
        onImportRowsChange?.(manualRows, {...fileRowChanges, excludedIndexes: []});
    }, [fileRowChanges, manualRows, onImportRowsChange]);
    const manualRowContext = useMemo<ManualRowContextValue>(() => ({
        isPending: isManualPreviewPending,
        isSupported: isManualRowsSupported,
        onAdd: handleManualRowAdd,
    }), [
        handleManualRowAdd,
        isManualPreviewPending,
        isManualRowsSupported,
    ]);
    const readyRows = effectiveRows.filter((row) => row.status === TimeTrackingImportRowStatus.Ready).length;
    const warningRows = effectiveRows.filter((row) => row.status === TimeTrackingImportRowStatus.Warning).length;
    const errorRows = effectiveRows.filter((row) => row.status === TimeTrackingImportRowStatus.Error).length;
    const stats = [
        {label: intl.formatMessage({id: Lx.TimeTrackingImport.PREVIEW_STAT_TOTAL}), value: preview.totalRows},
        {label: intl.formatMessage({id: Lx.TimeTrackingImport.PREVIEW_STAT_READY}), value: readyRows},
        {label: intl.formatMessage({id: Lx.TimeTrackingImport.PREVIEW_STAT_WARNINGS}), value: warningRows},
        {label: intl.formatMessage({id: Lx.TimeTrackingImport.PREVIEW_STAT_ERRORS}), value: errorRows},
        {
            label: intl.formatMessage({id: Lx.TimeTrackingImport.PREVIEW_STAT_UNRESOLVED}),
            value: remainingUnresolved.length,
        },
    ];

    useEffect(() => {
        if (!isManualRowsSupported) {
            return;
        }

        if (projects.length < 1) {
            void dispatch(getProjects());
        }

        if (shiftTypes.length < 1) {
            void dispatch(getAllShiftsTypes());
        }

        if (absenceTypes.length < 1) {
            void dispatch(getAllAbsencesTypes());
        }

        if (tagTypes.length < 1) {
            void dispatch(getAllTagsTypes());
        }
    }, [
        absenceTypes.length,
        dispatch,
        isManualRowsSupported,
        projects.length,
        shiftTypes.length,
        tagTypes.length,
    ]);

    return (
        <ManualRowProvider value={manualRowContext}>
            <section className={classNames(styles.card, styles.previewSummaryCard)}>
                <div className={styles.cardHeader}>
                    <div>
                        <h3>{intl.formatMessage({id: Lx.TimeTrackingImport.PREVIEW_TITLE})}</h3>
                        <p>{intl.formatMessage({id: Lx.TimeTrackingImport.PREVIEW_DESCRIPTION})}</p>
                    </div>
                </div>
                <div className={styles.statGrid}>
                    {stats.map((item) => (
                        <div key={item.label} className={styles.statCard}>
                            <span>{item.label}</span>
                            <strong>{item.value}</strong>
                        </div>
                    ))}
                </div>
                {fileRowChanges.excludedIndexes.length > 0 && (
                    <div className={styles.removedRowsNotice}>
                        <span>
                            {intl.formatMessage(
                                {id: Lx.TimeTrackingImport.REMOVED_ROWS_NOTICE},
                                {count: fileRowChanges.excludedIndexes.length}
                            )}
                        </span>
                        <button
                            type='button'
                            className={styles.removedRowsRestoreButton}
                            disabled={isManualPreviewPending}
                            onClick={handleRestoreRemovedRows}
                        >
                            {intl.formatMessage({id: Lx.TimeTrackingImport.REMOVED_ROWS_RESTORE})}
                        </button>
                    </div>
                )}
                {remainingUnresolved.length > 0 && (
                    <div className={styles.unresolvedBlock}>
                        <h4>{intl.formatMessage({id: Lx.TimeTrackingImport.PREVIEW_UNRESOLVED_VALUES})}</h4>
                        <div className={styles.unresolvedList}>
                            {remainingUnresolved.map((item) => (
                                <div key={`${item.field}-${item.value}`} className={styles.unresolvedItem}>
                                    <strong>{item.field}</strong>
                                    <span>{item.value}</span>
                                    <span>
                                        {intl.formatMessage(
                                            {id: Lx.TimeTrackingImport.PREVIEW_UNRESOLVED_ROWS},
                                            {rows: item.rowNumbers.join(', ')}
                                        )}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </section>

            <section className={styles.previewGridSection}>
                <div className={styles.previewGridWrapper}>
                    <div className={styles.previewGridHeaderWrapper}>
                        <div className={styles.previewGridCustomHeader} style={headerTemplateColumns}>
                            {headerCells}
                        </div>
                    </div>
                    <TimiDataGrid
                        ref={gridRef}
                        columns={columns}
                        rows={sortedRows as GridRowModel[]}
                        groupBy={groupBy}
                        intl={intl}
                        className={classNames(styles.previewGrid, {
                            [styles.previewGridClickableRows]: isManualRowsSupported,
                        })}
                        bodyClassName={styles.previewGridBody}
                        hideHeader
                        disableBodyScroll
                        initialExpandedRowKeys={employeeValues}
                        onRowClick={isManualRowsSupported ? handleRowClick : undefined}
                        onAnyRowExpand={setIsAnyRowExpanded}
                    />
                </div>
            </section>

            <TimeTrackingImportRowActionsMenu
                position={actionsMenu?.position ?? null}
                onEdit={() => actionsMenu && handleRowEdit(actionsMenu.row)}
                onRemove={() => actionsMenu && handleRowRemove(actionsMenu.row)}
                onClose={() => setActionsMenu(null)}
            />

            {editorState && (
                <TimeTrackingImportManualRowDialog
                    open
                    editedRow={editedRow}
                    employeeValue={editorState.employeeValue}
                    employeeValues={employeeValues}
                    defaultDate={editorDefaultDate}
                    options={manualRowOptions}
                    minDate={manualRowDateRange.minDate}
                    maxDate={manualRowDateRange.maxDate}
                    onSave={handleRowSave}
                    onClose={() => setEditorState(null)}
                />
            )}
        </ManualRowProvider>
    );
}
